import api from './api';

// Request OTP
// POST /auth/request-otp
// Request: { email: string }
// Response: { message: string }
export const requestOtp = async (email: string) => {
  try {
    const response = await api.post('/auth/request-otp', { email }, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to request OTP');
  }
};

// Verify OTP
// POST /auth/validate-otp
// Request: { email: string, otp: string }
// Response: { success: boolean, token: string, message: string }
export const verifyOtp = async (email: string, otp: string) => {
  try {
    const response = await api.post('/auth/validate-otp', { email, code: otp }, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to verify OTP');
  }
};

// Register user
// POST /auth/register
// Request: { email: string, password: string }
// Response: { success: boolean, message: string }
export const register = async (data: { email: string; password: string }) => {
  try {
    const response = await api.post('/auth/register', data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to register');
  }
};

// Logout user
// POST /auth/logout
// Response: { success: boolean }
export const logout = async () => {
  try {
    // Call the server to clear the cookie
    const response = await api.post('/auth/logout', {}, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to logout');
  }
};

// Accept privacy statement
// POST /auth/accept-privacy
// Response: { success: boolean }
export const acceptPrivacy = async () => {
  try {
    const response = await api.post('/auth/accept-privacy', {}, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to accept privacy statement');
  }
};

// Get privacy acceptance status
// GET /auth/privacy-status
// Response: { hasAcceptedPrivacy: boolean }
export const getPrivacyStatus = async () => {
  try {
    const response = await api.get('/auth/privacy-status', { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get privacy status');
  }
};

// Get subscription status
// GET /auth/subscription-status
// Response: { status: string, trialExpiresOn: Date, daysRemaining: number, hasSeenTrialNotice: boolean }
export const getSubscriptionStatus = async () => {
  try {
    const response = await api.get('/auth/subscription-status', { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get subscription status');
  }
};

// Acknowledge trial notice
// POST /auth/acknowledge-trial
// Response: { success: boolean }
export const acknowledgeTrialNotice = async () => {
  try {
    const response = await api.post('/auth/acknowledge-trial', {}, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to acknowledge trial notice');
  }
};

// Update subscription status
// POST /auth/update-subscription
// Request: { status: 'trial' | 'paid' | 'free' }
// Response: { success: boolean, status: string }
export const updateSubscriptionStatus = async (status: 'trial' | 'paid' | 'free') => {
  try {
    const response = await api.post('/auth/update-subscription', { status }, { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to update subscription status');
  }
};

// Get user info
// GET /auth/user-info
// Response: { userId: string, email: string }
export const getUserInfo = async () => {
  try {
    const response = await api.get('/auth/user-info', { withCredentials: true });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get user information');
  }
};
